import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddClientModal = ({ onClose, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [form, setForm] = useState({ name: '', phone: '', language: '' });

  const handleSubmit = async () => {
    if (!form.language) { toast.error(t('languageRequired')); return; }
    if (!form.name.trim()) { toast.error(t('error')); return; }
    try {
      await axios.post(`${API}/clients`, form);
      toast.success(t('success'));
      onSuccess();
      onClose();
    } catch { toast.error(t('error')); }
  };

  return (
    <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50">
        <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
          <User className="w-5 h-5 text-[#D4AF37]" />
          {t('addClient')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-slate-700">{t('clientName')} <span className="text-red-500">*</span></Label>
            <Input data-testid="client-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t('clientName')} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-slate-700">{t('phone')}</Label>
            <Input data-testid="client-phone-input" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder={t('phone')} className="mt-1.5" dir="ltr" />
          </div>
          <div>
            <Label className="text-slate-700">{t('language')} <span className="text-red-500">*</span></Label>
            <Select value={form.language} onValueChange={(val) => setForm({ ...form, language: val })}>
              <SelectTrigger data-testid="client-language-select" className="mt-1.5">
                <SelectValue placeholder={t('selectLanguage')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ar">
                  <div className="flex items-center gap-2"><Globe className="w-4 h-4" /><span>{t('arabic')} (العربية)</span></div>
                </SelectItem>
                <SelectItem value="en">
                  <div className="flex items-center gap-2"><Globe className="w-4 h-4" /><span>{t('english')} (English)</span></div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <Button data-testid="save-client-btn" onClick={handleSubmit} className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#0F172A]">{t('save')}</Button>
          <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
        </div>
      </CardContent>
    </Card>
  );
};
